<script>
  // @ts-nocheck
  import dayjs from 'dayjs';
  import { Cloud, UserRound, UsersRound } from 'lucide-svelte';
  import { avatarContentUrl } from '$lib/avatar';
  import MessagePreview from '@components/messenger/MessagePreview.svelte';

  export let conversation;
  export let selectedId;
  export let className = '';
  export let onSelect = () => {};

  let failedAvatarUrls = new Set();

  function getBrowserLocale() {
    if (typeof navigator === 'undefined') return 'vi-VN';
    return navigator.languages?.[0] || navigator.language || 'vi-VN';
  }

  function formatTime(value) {
    const sentAt = dayjs(Number(value) || value);
    if (!value || !sentAt.isValid()) return '';

    const now = dayjs();
    const locale = getBrowserLocale();
    const daysAgo = now.startOf('day').diff(sentAt.startOf('day'), 'day');

    if (daysAgo <= 0) {
      return new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit'
      }).format(sentAt.toDate());
    }

    if (daysAgo < 7) {
      const language = locale.toLowerCase().split('-')[0];
      if (language === 'vi') return `${daysAgo} ngày`;
      if (language === 'en') return `${daysAgo} ${daysAgo === 1 ? 'day' : 'days'}`;

      return new Intl.RelativeTimeFormat(locale, {
        numeric: 'always',
        style: 'short'
      }).format(-daysAgo, 'day');
    }

    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: '2-digit',
      ...(now.year() === sentAt.year() ? {} : { year: 'numeric' })
    }).format(sentAt.toDate());
  }

  //$: console.log('selectedId->conversation', selectedId, conversation);

  $: isSelected = selectedId === conversation.conversationId;
  $: conversationKind =
    conversation?.type === 'group' ? 'Nhóm' : conversation?.type === 'cloud' ? 'Cloud' : 'Trò chuyện';
  $: isGroup = conversation?.type === 'group';
  $: avatarUrl = avatarContentUrl(conversation?.avatar || conversation?.avatarUrl);
  $: groupPreviewMembers = Array.isArray(conversation?.memberPreview) ? conversation.memberPreview.slice(0, 3) : [];
  $: memberCount = Math.max(groupPreviewMembers.length, Number(conversation?.memberCount) || 0);
  $: groupCountBadge = memberCount > 3 ? memberCount - 3 : 0;

  function avatarIsAvailable(url) {
    return Boolean(url) && !failedAvatarUrls.has(url);
  }

  function markAvatarUnavailable(url) {
    if (!url || failedAvatarUrls.has(url)) return;
    failedAvatarUrls = new Set([...failedAvatarUrls, url]);
  }

  function memberAvatarUrl(member) {
    return avatarContentUrl(member?.avatar);
  }

  function getInitial(value = '') {
    return (
      String(value || '?')
        .trim()
        .charAt(0)
        .toUpperCase() || '?'
    );
  }
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_missing_attribute -->
<div
  onclick={() => onSelect(conversation)}
  class="p-2.5 rounded-xl mb-1 cursor-pointer transition-all select-none relative
    flex items-center gap-3 border border-transparent
    hover:bg-white hover:shadow-sm active:scale-[0.985]
    {isSelected ? 'bg-blue-50 border-blue-200 shadow-sm' : ''} {className}
  ">
  <!-- Avatar -->
  <div
    class="w-11 h-11 shrink-0 flex items-center justify-center
         overflow-hidden relative border border-slate-200/90
         font-semibold text-sm uppercase
         {isGroup ? 'rounded-2xl bg-violet-50 text-violet-600' : 'rounded-full'}">
    {#if isGroup && !avatarIsAvailable(avatarUrl)}
      {#if groupPreviewMembers.length}
        <div class="grid h-full w-full grid-cols-2 gap-px overflow-hidden bg-white">
          {#each groupPreviewMembers as member}
            {@const previewAvatarUrl = memberAvatarUrl(member)}
            <div
              class="relative flex min-h-0 min-w-0 items-center justify-center overflow-hidden bg-slate-100 text-[10px] font-semibold text-slate-500">
              {#if avatarIsAvailable(previewAvatarUrl)}
                <img
                  src={previewAvatarUrl}
                  alt={member?.name || 'Thành viên nhóm'}
                  class="h-full w-full object-cover"
                  loading="lazy"
                  decoding="async"
                  onerror={() => markAvatarUnavailable(previewAvatarUrl)} />
              {:else}
                <span>{getInitial(member?.name)}</span>
              {/if}
            </div>
          {/each}

          {#if groupCountBadge}
            <span
              class="absolute bottom-0 right-0 grid min-h-[1.55rem] min-w-[1.55rem] place-items-center rounded-full border-2 border-white bg-slate-100 px-1 text-[10px] font-bold leading-none text-slate-700 shadow-sm">
              {groupCountBadge}
            </span>
          {/if}
        </div>
      {:else}
        <UsersRound size={21} strokeWidth={2.1} />
      {/if}
    {:else if avatarIsAvailable(avatarUrl)}
      <img
        src={avatarUrl}
        alt={conversation?.title || 'Avatar cuộc trò chuyện'}
        class="h-full w-full object-cover"
        loading="lazy"
        decoding="async"
        onerror={() => markAvatarUnavailable(avatarUrl)} />
    {:else if conversationKind === 'Cloud'}
      <div
        class="absolute inset-0
                bg-gradient-to-br from-blue-400 to-indigo-500">
      </div>

      <Cloud class="relative text-white opacity-95" size={21} strokeWidth={2.2} />
    {:else if conversationKind === 'Nhóm'}
      <UsersRound size={21} strokeWidth={2.1} />
    {:else}
      <div
        class="w-full h-full flex items-center justify-center
                bg-sky-50 text-sky-600">
        <UserRound size={21} strokeWidth={2.1} />
        <span class="sr-only">{getInitial(conversation?.title)}</span>
      </div>
    {/if}
  </div>

  <!-- Content -->
  <div class="flex-1 min-w-0">
    <!-- Top row -->
    <div class="flex items-center justify-between gap-2">
      <div class="min-w-0 flex items-center gap-2">
        <span class="font-medium text-gray-800 truncate">
          {conversation.title}
        </span>
        {#if conversation.type !== 'cloud'}
          <span class="shrink-0 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500 hidden">
            {conversationKind}
          </span>
        {/if}
      </div>

      {#if conversation.lastMessageAt}
        <span class="text-[11px] text-gray-400 whitespace-nowrap">
          {formatTime(conversation.lastMessageAt)}
        </span>
      {/if}
    </div>

    <!-- Message preview -->
    <MessagePreview preview={conversation.lastMessagePreview} />
  </div>

  <!-- Online dot -->
  {#if conversation.type === 'direct'}
    <div
      class="
        w-2.5 h-2.5 rounded-full absolute right-0 bottom-0 border-2 border-white
        {conversation.online ? 'bg-green-500' : ''}
      ">
    </div>
  {/if}
</div>
